import React from 'react';
import { Card, CardContent } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import { FlameIcon, ClockIcon, CalendarIcon, TrendingUpIcon, ZapIcon, UsersIcon } from 'lucide-react';
import type { PeakDay, PeakHour, ChannelBreakdown } from '../../lib/metrics-hooks';
interface MetricsPeakInsightsProps {
  peakDays: PeakDay[];
  peakHours: PeakHour[];
  channelBreakdown: ChannelBreakdown;
  totalOrders: number;
  totalRevenue: number;
  averageTicket: number;
}
export function MetricsPeakInsights({
  peakDays,
  peakHours,
  channelBreakdown,
  totalOrders,
  totalRevenue,
  averageTicket
}: MetricsPeakInsightsProps) {
  const totalChannel = channelBreakdown.qr + channelBreakdown.waiter + channelBreakdown.other;
  const qrPct = totalChannel > 0 ? Math.round(channelBreakdown.qr / totalChannel * 100) : 0;
  const waiterPct = totalChannel > 0 ? Math.round(channelBreakdown.waiter / totalChannel * 100) : 0;
  return <div className="space-y-4" data-id="element-898">
      {/* Summary Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-899">
        <Card data-id="element-900">
          <CardContent className="p-5" data-id="element-901">
            <div className="flex items-start gap-3" data-id="element-902">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-903">
                <TrendingUpIcon className="w-5 h-5 text-emerald-600" data-id="element-904" />
              </div>
              <div className="min-w-0" data-id="element-905">
                <p className="text-sm text-gray-500 font-medium" data-id="element-906">
                  Ingresos Totales
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-907">
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-908">
          <CardContent className="p-5" data-id="element-909">
            <div className="flex items-start gap-3" data-id="element-910">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-911">
                <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-912" />
              </div>
              <div className="min-w-0" data-id="element-913">
                <p className="text-sm text-gray-500 font-medium" data-id="element-914">
                  Total Órdenes
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5" data-id="element-915">
                  {totalOrders}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-916">
          <CardContent className="p-5" data-id="element-917">
            <div className="flex items-start gap-3" data-id="element-918">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-919">
                <ZapIcon className="w-5 h-5 text-purple-600" data-id="element-920" />
              </div>
              <div className="min-w-0" data-id="element-921">
                <p className="text-sm text-gray-500 font-medium" data-id="element-922">
                  Ticket Promedio
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-923">
                  {formatCurrency(averageTicket)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Peak Insights Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-924">
        {/* Peak Day */}
        <Card data-id="element-925">
          <CardContent className="p-5" data-id="element-926">
            <div className="flex items-center gap-2 mb-3" data-id="element-927">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center" data-id="element-928">
                <FlameIcon className="w-4 h-4 text-red-500" data-id="element-929" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-930">
                Día Pico
              </span>
            </div>
            {peakDays.length > 0 ? <div className="space-y-2" data-id="element-931">
                {peakDays.map((day, i) => <div key={day.date} className="flex items-center justify-between" data-id="element-932">
                    <div className="flex items-center gap-2" data-id="element-933">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-934">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-935">{day.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-936">
                      {day.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-937">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Peak Hour */}
        <Card data-id="element-938">
          <CardContent className="p-5" data-id="element-939">
            <div className="flex items-center gap-2 mb-3" data-id="element-940">
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-941">
                <ClockIcon className="w-4 h-4 text-amber-600" data-id="element-942" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-943">
                Hora Pico
              </span>
            </div>
            {peakHours.length > 0 ? <div className="space-y-2" data-id="element-944">
                {peakHours.map((hour, i) => <div key={hour.hour} className="flex items-center justify-between" data-id="element-945">
                    <div className="flex items-center gap-2" data-id="element-946">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-947">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-948">
                        {hour.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-949">
                      {hour.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-950">Sin datos</p>}
          </CardContent>
        </Card>

        {/* QR Self-service */}
        <Card data-id="element-951">
          <CardContent className="p-5" data-id="element-952">
            <div className="flex items-center gap-2 mb-3" data-id="element-953">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-954">
                <CalendarIcon className="w-4 h-4 text-emerald-600" data-id="element-955" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-956">
                Autoservicio QR
              </span>
            </div>
            <div className="space-y-1" data-id="element-957">
              <p className="text-2xl font-bold text-emerald-600" data-id="element-958">
                {channelBreakdown.qr}
              </p>
              <p className="text-sm text-gray-500" data-id="element-959">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-960">{qrPct}%</span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-961">
                {formatCurrency(channelBreakdown.qrRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Waiter */}
        <Card data-id="element-962">
          <CardContent className="p-5" data-id="element-963">
            <div className="flex items-center gap-2 mb-3" data-id="element-964">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-965">
                <UsersIcon className="w-4 h-4 text-blue-600" data-id="element-966" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-967">
                Mesero
              </span>
            </div>
            <div className="space-y-1" data-id="element-968">
              <p className="text-2xl font-bold text-blue-600" data-id="element-969">
                {channelBreakdown.waiter}
              </p>
              <p className="text-sm text-gray-500" data-id="element-970">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-971">
                  {waiterPct}%
                </span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-972">
                {formatCurrency(channelBreakdown.waiterRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}